import React, { useState, useEffect } from 'react';
import { Route, MapPin, Calendar, Clock, User, Truck, Plus, Filter, Share2, Copy } from 'lucide-react';
import saasApiService from '../../../services/saasApi';
import MapRoutePlanner from './MapRoutePlanner';

interface RouteData {
  id: number;
  route_name?: string;
  client_id: number;
  client_name?: string;
  driver_id: number;
  driver_name?: string;
  vehicle_id: number;
  vehicle_info?: string;
  origin_address?: string;
  destination_address?: string;
  origin: string;
  destination: string;
  scheduled_date: string;
  estimated_duration_minutes?: number;
  estimated_duration: number;
  distance_km: number;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  notes?: string;
  actual_start_time?: string;
  actual_end_time?: string;
  route_data?: string;
}

const RouteManager: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'list' | 'map'>('list');
  const [routes, setRoutes] = useState<RouteData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  useEffect(() => {
    fetchRoutes();
    
    // Listen for route saved events from MapRoutePlanner
    const handleRouteSaved = () => {
      fetchRoutes();
    };
    window.addEventListener('routeSaved', handleRouteSaved);
    
    return () => {
      window.removeEventListener('routeSaved', handleRouteSaved);
    };
  }, []);

  const fetchRoutes = async () => {
    try {
      setLoading(true);
      const response = await saasApiService.getRoutes();
      let routeData = [];
      if (response && response.routes && Array.isArray(response.routes)) {
        routeData = response.routes;
      } else if (Array.isArray(response)) {
        routeData = response;
      }
      setRoutes(routeData);
    } catch (error) {
      console.error('Error fetching routes:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'scheduled': return 'Programada';
      case 'in_progress': return 'En Progreso';
      case 'completed': return 'Completada';
      case 'cancelled': return 'Cancelada';
      default: return status;
    }
  };

  // Actions to start/complete a route
  const startRouteAction = async (id: number) => {
    try {
      setActionLoading(id);
      await saasApiService.startRoute(id);
      await fetchRoutes();
    } catch (e: any) {
      alert(`No se pudo iniciar la ruta: ${e?.response?.data?.message || e.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const completeRouteAction = async (id: number) => {
    try {
      setActionLoading(id);
      await saasApiService.completeRoute(id);
      await fetchRoutes();
    } catch (e: any) {
      alert(`No se pudo completar la ruta: ${e?.response?.data?.message || e.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const shareRoute = async (route: RouteData) => {
    try {
      let routeData: any | undefined;
      if (route.route_data) {
        try {
          routeData = JSON.parse(route.route_data);
        } catch (e) {
          console.error('Error parsing route data:', e);
        }
      }

      const origin = route.origin_address || route.origin;
      const destination = route.destination_address || route.destination;

      // Normalize waypoints from several possible shapes
      const getWaypointAddresses = (): string[] => {
        const addresses: string[] = [];

        // 0) Sometimes routes list includes waypoints at top-level
        const listWps: any[] | undefined = (route as any).waypoints;
        if (Array.isArray(listWps)) {
          for (const w of listWps) {
            const addr = typeof w === 'string' ? w : (w?.address ?? undefined);
            if (addr) addresses.push(addr);
          }
        }

        if (!routeData) return addresses;

        // 1) Top-level waypoints could be array of objects or strings
        if (Array.isArray(routeData.waypoints)) {
          for (const wp of routeData.waypoints) {
            if (typeof wp === 'string') {
              addresses.push(wp);
            } else if (wp && typeof wp === 'object' && 'address' in wp) {
              // if object, only include non-origin points
              const addr = (wp as any).address;
              const type = (wp as any).type;
              if (addr && type !== 'origin') addresses.push(addr);
            }
          }
        }

        // 2) Optimized structure could exist
        const opt: any = routeData.optimized_route;
        if (opt) {
          // common shapes: ordered_waypoints: string[] OR waypoints: string[]
          const ordered = (opt.ordered_waypoints || opt.waypoints || []) as any[];
          for (const w of ordered) {
            if (typeof w === 'string') {
              if (!addresses.includes(w)) addresses.push(w);
            } else if (w && typeof w === 'object' && 'address' in w) {
              const addr = (w as any).address;
              if (addr && !addresses.includes(addr)) addresses.push(addr);
            }
          }

          // fallback: derive from legs -> take each leg's end_address except the final destination
          if (Array.isArray(opt.legs)) {
            for (let i = 0; i < opt.legs.length - 1; i++) {
              const leg = opt.legs[i];
              const end = leg?.end_address;
              if (end && end !== origin && !addresses.includes(end)) {
                addresses.push(end);
              }
            }
          }
        }

        // Remove origin if accidentally included, keep unique order
        return addresses.filter(a => a && a !== origin);
      };

      let waypointAddresses = getWaypointAddresses();
      console.log('shareRoute: extracted waypointAddresses (initial):', waypointAddresses);

      // If no waypoints found, try fetching full route detail
      if (waypointAddresses.length === 0 && route.id) {
        try {
          const full = await saasApiService.getRoute(route.id);
          // Normalize common response shapes
          const fullRoute: any = (full && (full.route || full.data || full)) || {};

          // Try top-level waypoints array on the route itself
          if (Array.isArray(fullRoute.waypoints) && waypointAddresses.length === 0) {
            waypointAddresses = (fullRoute.waypoints as any[])
              .map((w: any) => (typeof w === 'string' ? w : w?.address))
              .filter(Boolean)
              .filter((a: string) => a !== origin);
          }

          // Try route_data field (string or object)
          let fullData: any | undefined;
          if (fullRoute?.route_data) {
            try { fullData = typeof fullRoute.route_data === 'string' ? JSON.parse(fullRoute.route_data) : fullRoute.route_data; } catch {}
          }
          if (fullData) {
            const tmp = routeData; // keep original
            routeData = fullData;
            const fromFull = getWaypointAddresses();
            if (fromFull.length > 0) waypointAddresses = fromFull;
            routeData = tmp; // restore
          }
        } catch (e) {
          console.warn('No se pudo cargar detalles de la ruta para compartir:', e);
        }
      }

      // Prefer a server-persisted google_maps_link if present
      const rowLink: string | undefined = (route as any).google_maps_link;
      // Prefer the exact link generated during planning if available in route_data
      const plannedLink: string | undefined = rowLink || (() => {
        try {
          return routeData?.optimized_route?.maps_link as string | undefined;
        } catch { return undefined; }
      })();

      let mapsUrl = plannedLink || '';
      if (!plannedLink) {
        // Ask backend to generate canonical link to avoid mismatches
        const ordered: string[] = [origin, ...waypointAddresses];
        if (destination && destination !== ordered[ordered.length - 1]) {
          ordered.push(destination);
        }
        try {
          const gen = await saasApiService.generateMapsLink(ordered);
          mapsUrl = gen?.maps_link || gen?.link || gen || '';
        } catch (e) {
          // Fallback to local construction if backend generation fails
          mapsUrl = `https://www.google.com/maps/dir/${ordered.map(a => encodeURIComponent(a)).join('/')}`;
        }
      }
      console.log('shareRoute: final mapsUrl', mapsUrl);

      try {
        await navigator.clipboard.writeText(mapsUrl);
        alert('¡Link de la ruta copiado al portapapeles!');
      } catch (clipErr) {
        console.warn('Clipboard not available, opening link:', clipErr);
        window.open(mapsUrl, '_blank');
      }
    } catch (error) {
      console.error('Error sharing route:', error);
      alert('Error al generar el link de la ruta');
    }
  };

  // Delete a saved route with confirmation and refresh
  const deleteRoute = async (route: RouteData) => {
    if (!window.confirm(`¿Eliminar la ruta "${route.route_name || 'Ruta #' + route.id}"?`)) return;
    try {
      // Optimistic UI: remove locally
      setRoutes(prev => prev.filter(r => r.id !== route.id));
      await saasApiService.deleteRoute(route.id as any);
      // Ensure sync with backend
      fetchRoutes();
    } catch (e: any) {
      alert(`No se pudo eliminar la ruta: ${e?.response?.data?.message || e.message}`);
      // Re-sync list in case of failure
      fetchRoutes();
    }
  };

  const filteredRoutes = routes.filter(route => {
    if (!searchQuery.trim()) return true;
    const name = (route.route_name || `Ruta #${route.id}` || '').toLowerCase();
    const client = (route.client_name || '').toLowerCase();
    const q = searchQuery.toLowerCase();
    return name.includes(q) || client.includes(q);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Gestión de Rutas</h1>
        <div className="text-sm text-gray-600">
          Usa el <span className="font-medium text-purple-600">Mapa Interactivo</span> para crear nuevas rutas
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('list')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'list'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Lista de Rutas
          </button>
          <button
            onClick={() => setActiveTab('map')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'map'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Mapa Interactivo
          </button>
        </nav>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'map' ? (
        <MapRoutePlanner />
      ) : (
        <>
          {/* Search by route name */}
          <div className="flex space-x-4">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por nombre de ruta..."
              className="border border-gray-300 rounded-md px-3 py-2 text-sm w-full max-w-md"
            />
          </div>

          {/* Routes List */}
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            {filteredRoutes.length === 0 ? (
              <div className="text-center py-12">
                <Route className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No hay rutas</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Comienza agregando tu primera ruta.
                </p>
                <div className="mt-6">
                  <button 
                    onClick={() => setActiveTab('map')}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Crear Ruta en Mapa
                  </button>
                </div>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {filteredRoutes.map((route) => (
                  <li key={route.id} className="px-6 py-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {/* Route Name and Status */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center">
                            <MapPin className="h-5 w-5 text-blue-500 mr-2" />
                            <h3 className="text-lg font-semibold text-gray-900">
                              {route.route_name || `Ruta #${route.id}`}
                            </h3>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => shareRoute(route)}
                              className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              title="Copiar link de Google Maps"
                            >
                              <Copy className="h-4 w-4 mr-1" />
                              Copiar link
                            </button>
                            {route.status === 'scheduled' && (
                              <button
                                onClick={() => startRouteAction(route.id)}
                                className="inline-flex items-center px-3 py-1 border border-blue-300 rounded-md text-sm font-medium text-blue-700 bg-white hover:bg-blue-50 disabled:opacity-50"
                                disabled={actionLoading === route.id}
                                title="Iniciar ruta"
                              >
                                {actionLoading === route.id ? 'Iniciando…' : 'Iniciar'}
                              </button>
                            )}
                            {route.status === 'in_progress' && (
                              <button
                                onClick={() => completeRouteAction(route.id)}
                                className="inline-flex items-center px-3 py-1 border border-green-300 rounded-md text-sm font-medium text-green-700 bg-white hover:bg-green-50 disabled:opacity-50"
                                disabled={actionLoading === route.id}
                                title="Completar ruta"
                              >
                                {actionLoading === route.id ? 'Completando…' : 'Completar'}
                              </button>
                            )}
                            <button
                              onClick={() => deleteRoute(route)}
                              className="inline-flex items-center px-3 py-1 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                              title="Eliminar ruta"
                            >
                              Eliminar
                            </button>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(route.status)}`}>
                              {getStatusText(route.status)}
                            </span>
                          </div>
                        </div>

                        {/* Origin and Destination */}
                        <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                          <div className="text-sm text-gray-600 mb-1">Ruta:</div>
                          <div className="text-sm font-medium text-gray-900">
                            <span className="text-green-600">📍 Origen:</span> {route.origin_address || route.origin}
                          </div>
                          <div className="text-sm font-medium text-gray-900 mt-1">
                            <span className="text-red-600">🏁 Destino:</span> {route.destination_address || route.destination}
                          </div>
                        </div>
                        
                        {/* Route Details */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-gray-600 mb-3">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-2 text-blue-500" />
                            <div>
                              <div className="font-medium">Fecha</div>
                              <div>{new Date(route.scheduled_date).toLocaleDateString('es-PE')}</div>
                            </div>
                          </div>
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 mr-2 text-green-500" />
                            <div>
                              <div className="font-medium">Duración</div>
                              <div>{Math.round((route.estimated_duration_minutes || route.estimated_duration) / 60 * 10) / 10} horas</div>
                            </div>
                          </div>
                          <div className="flex items-center">
                            <Route className="h-4 w-4 mr-2 text-purple-500" />
                            <div>
                              <div className="font-medium">Distancia</div>
                              <div>{route.distance_km} km</div>
                            </div>
                          </div>
                          {route.client_name && (
                            <div className="flex items-center">
                              <User className="h-4 w-4 mr-2 text-orange-500" />
                              <div>
                                <div className="font-medium">Cliente</div>
                                <div>{route.client_name}</div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Notes */}
                        {route.notes && (
                          <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                            <div className="text-sm font-medium text-blue-900 mb-1">Notas:</div>
                            <div className="text-sm text-blue-800">{route.notes}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

        </>
      )}
    </div>
  );
};

export default RouteManager;
